import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createApiClient } from "../client/index.ts";
import type { CourseKitApi } from "../schema/api.ts";
import type { Locale, LocalizedText } from "../schema/core.ts";
import { detectLocale, storeLocale } from "./locale.ts";

export interface CourseSiteBrand {
  name: string;
  tagline?: LocalizedText;
  logoSrc?: string;
  /** The main website, linked from the header and footer. */
  homeUrl?: string;
}

interface CourseKitContextValue {
  api: CourseKitApi;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  brand: CourseSiteBrand;
}

const CourseKitContext = createContext<CourseKitContextValue | null>(null);

export const createCourseQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false },
    },
  });

interface CourseKitProviderProps {
  brand: CourseSiteBrand;
  /** Origin of the course Worker; omit for same-origin `/api`. */
  apiBaseUrl?: string;
  /** Supply your own client to share a cache; one is created otherwise. */
  queryClient?: QueryClient;
  children: ReactNode;
}

/** API client, React Query cache, visitor locale and brand for every kit component. */
export const CourseKitProvider = ({
  brand,
  apiBaseUrl,
  queryClient,
  children,
}: CourseKitProviderProps): ReactNode => {
  const [ownQueryClient] = useState(createCourseQueryClient);
  const [locale, setLocaleState] = useState<Locale>(detectLocale);
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<CourseKitContextValue>(
    () => ({
      api,
      locale,
      setLocale: (next): void => {
        storeLocale(next);
        setLocaleState(next);
      },
      brand,
    }),
    [api, locale, brand],
  );

  return (
    <QueryClientProvider client={queryClient ?? ownQueryClient}>
      <CourseKitContext value={value}>{children}</CourseKitContext>
    </QueryClientProvider>
  );
};

export const useCourseKit = (): CourseKitContextValue => {
  const context = useContext(CourseKitContext);
  if (!context) throw new Error("useCourseKit must be used inside <CourseKitProvider>");
  return context;
};
